// arc/pages/admin/Settings.jsx 
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  useTheme,
  alpha,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SettingsBrightnessIcon,
  Palette as PaletteIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import { sendPasswordResetEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential, deleteUser, updateProfile } from 'firebase/auth';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const Settings = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { mode, toggleColorMode, primaryColor, setPrimaryColor } = useCustomTheme();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [profileData, setProfileData] = useState({
    displayName: currentUser?.displayName || '',
    photoURL: currentUser?.photoURL || '',
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const primaryColors = [
    { name: 'Default Blue', value: '#1976d2', label: 'Blue' },
    { name: 'Purple', value: '#9c27b0', label: 'Purple' },
    { name: 'Green', value: '#2e7d32', label: 'Green' },
    { name: 'Orange', value: '#ed6c02', label: 'Orange' },
    { name: 'Red', value: '#d32f2f', label: 'Red' },
    { name: 'Teal', value: '#009688', label: 'Teal' },
    { name: 'Pink', value: '#c2185b', label: 'Pink' },
    { name: 'Indigo', value: '#3f51b5', label: 'Indigo' },
  ];

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdateProfile = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // Update Firebase Auth profile
      await updateProfile(currentUser, {
        displayName: profileData.displayName,
        photoURL: profileData.photoURL,
      });

      // Update Firestore user document
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: profileData.displayName,
        photoURL: profileData.photoURL,
        updatedAt: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success',
      });
      setIsEditingProfile(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update profile',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!currentUser?.email) {
      setSnackbar({
        open: true,
        message: 'No email address found',
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(currentUser.email);
      setSnackbar({
        open: true,
        message: 'Password reset email sent! Check your inbox.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error sending reset email:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send reset email',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setResetDialogOpen(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentUser) return;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'New passwords do not match',
        severity: 'error',
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: 'Password must be at least 6 characters',
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      
      await updatePassword(currentUser, passwordData.newPassword);
      
      setSnackbar({
        open: true,
        message: 'Password updated successfully!',
        severity: 'success',
      });
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        setSnackbar({
          open: true,
          message: 'Current password is incorrect',
          severity: 'error',
        });
      } else if (error.code === 'auth/requires-recent-login') {
        setSnackbar({
          open: true,
          message: 'Please log in again before changing your password',
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: error.message || 'Failed to update password',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    if (deleteConfirmText !== 'DELETE') {
      setSnackbar({
        open: true,
        message: 'Please type "DELETE" to confirm account deletion',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      // Delete user data from Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await deleteDoc(userRef);
      
      // Delete user from Firebase Auth
      await deleteUser(currentUser);
      
      setSnackbar({
        open: true,
        message: 'Account deleted successfully',
        severity: 'success',
      });
      
      setTimeout(() => {
        window.location.href = '/auth/sign-in';
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        setSnackbar({
          open: true,
          message: 'Please log in again before deleting your account',
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: error.message || 'Failed to delete account',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };



 
  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: theme.palette.text.primary }}>
        Account Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account security, appearance, and preferences
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Profile Information Section */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: theme.palette.primary.main }} />
            Profile Information
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={profileData.photoURL || currentUser?.photoURL}
                sx={{ width: 100, height: 100, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
              >
                {!profileData.photoURL && !currentUser?.photoURL && (
                  <PersonIcon sx={{ fontSize: 50 }} />
                )}
              </Avatar>
              {isEditingProfile && (
                <TextField
                  size="small"
                  name="photoURL"
                  label="Photo URL"
                  value={profileData.photoURL}
                  onChange={handleProfileChange}
                  fullWidth
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            
            <Box sx={{ flex: 1 }}>
              {isEditingProfile ? (
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Display Name"
                    name="displayName"
                    value={profileData.displayName}
                    onChange={handleProfileChange}
                    placeholder="Enter your display name"
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      onClick={handleUpdateProfile}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsEditingProfile(false);
                        setProfileData({
                          displayName: currentUser?.displayName || '',
                          photoURL: currentUser?.photoURL || '',
                        });
                      }}
                      startIcon={<CancelIcon />}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Display Name"
                    value={currentUser?.displayName || 'Not set'}
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={currentUser?.email || ''}
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditingProfile(true)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Edit Profile
                  </Button>
                </Stack>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Appearance Section */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaletteIcon sx={{ color: theme.palette.primary.main }} />
            Appearance
          </Typography>
          
          <Stack spacing={3}>
            {/* Theme Mode Toggle */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                Theme Mode
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose your preferred theme appearance
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 1 }}>
                <Button
                  variant={mode === 'light' ? 'contained' : 'outlined'}
                  onClick={() => toggleColorMode('light')}
                  startIcon={<LightModeIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Light Mode
                </Button>
                <Button
                  variant={mode === 'dark' ? 'contained' : 'outlined'}
                  onClick={() => toggleColorMode('dark')}
                  startIcon={<DarkModeIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Dark Mode
                </Button>
                <Button
                  variant={mode === 'system' ? 'contained' : 'outlined'}
                  onClick={() => toggleColorMode('system')}
                  startIcon={<SettingsBrightnessIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  System Default
                </Button>
              </Stack>
            </Box>

            {/* Primary Color Selector */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                Primary Color
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose your accent color for the application
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1 }}>
                {primaryColors.map((color) => (
                  <Tooltip key={color.value} title={color.name}>
                    <IconButton
                      onClick={() => setPrimaryColor(color.value)}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: color.value,
                        border: primaryColor === color.value ? `3px solid ${theme.palette.common.white}` : 'none',
                        outline: primaryColor === color.value ? `2px solid ${color.value}` : 'none',
                        '&:hover': {
                          bgcolor: color.value,
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s',
                        boxShadow: theme.shadows[2],
                      }}
                    >
                      {primaryColor === color.value && (
                        <CheckCircleIcon sx={{ fontSize: 20, color: 'white' }} />
                      )}
                    </IconButton>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {/* Account Information */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon sx={{ color: theme.palette.primary.main }} />
            Account Information
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Email Address"
              value={currentUser?.email || ''}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              helperText="Your email address cannot be changed"
            />
            <TextField
              fullWidth
              label="User ID"
              value={currentUser?.uid || ''}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon color="action" />
                  </InputAdornment>
                ),
              }}
              helperText="Your unique account identifier"
            />
            <TextField
              fullWidth
              label="Account Created"
              value={currentUser?.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'N/A'}
              disabled
              helperText="The date your account was created"
            />
          </Stack>
        </Paper>

        {/* Password Management */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon sx={{ color: theme.palette.primary.main }} />
            Password Management
          </Typography>
          
          <Stack spacing={3}>
            <TextField
              fullWidth
              type={showCurrentPassword ? 'text' : 'password'}
              label="Current Password"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                      {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              helperText="Password must be at least 6 characters"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SecurityIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm New Password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SecurityIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              variant="contained"
              onClick={handleUpdatePassword}
              disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
              sx={{
                alignSelf: 'flex-start',
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
              }}
            >
              Update Password
            </Button>
          </Stack>
        </Paper>

        {/* Account Actions */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Account Actions
          </Typography>
          
          <Stack spacing={3}>
            {/* Forgot Password */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                Forgot Password?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Send a password reset link to your email address
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setResetDialogOpen(true)}
                disabled={loading}
                startIcon={<EmailIcon />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Send Reset Email
              </Button>
            </Box>
            
            <Divider />
            
            {/* Delete Account */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: '#ef4444' }}>
                Delete Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permanently delete your account and all associated data. This action cannot be undone.
              </Typography>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={loading}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Delete Account
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Reset Password
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            We'll send a password reset link to:
          </DialogContentText>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            <strong>{currentUser?.email}</strong>
          </Alert>
          <DialogContentText variant="body2" color="text.secondary">
            Click confirm to receive an email with instructions to reset your password.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button onClick={() => setResetDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSendResetEmail}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Send Reset Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 450 } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ef4444' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ef4444' }}>
            Delete Account
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This action is <strong>permanent and cannot be undone</strong>. All your data will be permanently deleted including:
          </DialogContentText>
          <Box sx={{ mb: 3, pl: 2 }}>
            <Typography variant="body2" component="li">• All courses you've created</Typography>
            <Typography variant="body2" component="li">• All books and resources</Typography>
            <Typography variant="body2" component="li">• Your profile information</Typography>
            <Typography variant="body2" component="li">• All enrolled course records</Typography>
          </Box>
          
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            Type <strong>"DELETE"</strong> to confirm account deletion
          </Alert>
          
          <TextField
            fullWidth
            label="Confirm with DELETE"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE here"
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            variant="contained"
            color="error"
            disabled={loading || deleteConfirmText !== 'DELETE'}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
          icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : null}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;